/**
 * SetupCheckWrapper
 *
 * This component checks if the system is set up before rendering the app.
 * If no tenants or users exist, it shows a "Setup Required" page with instructions.
 */

import type { JSX } from 'react';
import React from 'react';

import { useSetupStatus } from '../../hooks/useSetupStatus';
import { SetupRequired } from '../../pages/SetupRequired/SetupRequired';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { EndUserLoading, isEndUserPage } from '../EndUserLoading/EndUserLoading';

interface SetupCheckWrapperProps {
  children: React.ReactNode;
}

export function SetupCheckWrapper({ children }: SetupCheckWrapperProps): JSX.Element {
  const {
    setupComplete,
    hasTenants,
    hasUsers,
    users,
    tenants,
    environment,
    isLoading,
    error,
  } = useSetupStatus();

  // Show loading while checking setup status
  if (isLoading) {
    return isEndUserPage() ? <EndUserLoading /> : <LoadingOverlay />;
  }

  // If there was an error checking status, show the app anyway
  // (don't block users if the health check fails)
  if (error) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  // If setup is not complete, show the setup required page
  if (!setupComplete) {
    return (
      <SetupRequired
        hasTenants={hasTenants}
        hasUsers={hasUsers}
        users={users}
        tenants={tenants}
        environment={environment}
      />
    );
  }

  // Setup is complete, render the app
  return <React.Fragment>{children}</React.Fragment>;
}

export default SetupCheckWrapper;
